import React, { useState, useRef } from 'react';
import MentorshipAccessGate from '@/components/MentorshipAccessGate';
import { useLocation } from 'wouter';
import { LectureDownloader } from '@/components/LectureDownloader';
import { 
  ChevronLeft, 
  ChevronRight, 
  Maximize, 
  Minimize, 
  X, 
  Play, 
  CheckCircle2, 
  FileText, 
  ExternalLink 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Card } from '@/components/ui/card';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';

// Key takeaway component for highlighting important points
const KeyTakeaway = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="bg-blue-50 border-l-4 border-blue-500 p-4 my-6 rounded-r-md">
      <div className="flex">
        <CheckCircle2 className="h-6 w-6 text-blue-500 flex-shrink-0 mr-3 mt-1" />
        <div className="text-blue-800">{children}</div>
      </div>
    </div>
  );
};

// Scientific diagram component styled like Cell/Nature journals
const ScientificDiagram = ({ 
  title, 
  children, 
  caption,
  labelPosition = "top"
}: { 
  title: string; 
  children: React.ReactNode; 
  caption?: string;
  labelPosition?: "top" | "bottom";
}) => {
  return (
    <figure className="my-8 bg-white border rounded-md overflow-hidden shadow-sm">
      {labelPosition === "top" && (
        <div className="bg-gray-50 px-4 py-2 border-b">
          <div className="font-medium text-sm text-gray-900">Fig. {title}</div>
        </div>
      )}
      
      <div className="p-4">
        {children}
      </div>
      
      {(caption || labelPosition === "bottom") && (
        <div className="bg-gray-50 px-4 py-2 border-t">
          {labelPosition === "bottom" && (
            <div className="font-medium text-sm text-gray-900 mb-1">Fig. {title}</div>
          )}
          {caption && (
            <p className="text-xs text-gray-600 leading-snug">{caption}</p>
          )}
        </div>
      )}
    </figure>
  );
};

// Slide interface
interface Slide {
  id: number;
  title: string;
  content: React.ReactNode;
  image?: string;
}

// Slideshow component
const Slideshow = ({ slides }: { slides: Slide[] }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const slideshowRef = useRef<HTMLDivElement>(null);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      if (slideshowRef.current?.requestFullscreen) {
        slideshowRef.current.requestFullscreen();
      }
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
      setIsFullscreen(false);
    }
  };

  // Add fullscreen change event listener
  React.useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        setIsFullscreen(false);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  if (slides.length === 0) return null;

  return (
    <div 
      ref={slideshowRef} 
      className={`relative overflow-hidden rounded-lg border ${isFullscreen ? 'bg-white flex flex-col h-screen' : 'bg-gray-50'}`}
    >
      {/* Slide content */}
      <div className="relative flex-grow">
        <div className={`p-6 ${isFullscreen ? 'h-[calc(100vh-70px)] overflow-y-auto' : 'max-h-[70vh] overflow-y-auto'} thin-scrollbar`}>
          <div className="mb-4 flex justify-between items-center">
            <h3 className="text-xl font-medium text-gray-900">
              {slides[currentSlide].title}
            </h3>
            <div className="flex space-x-2">
              <button 
                onClick={toggleFullscreen}
                className="p-1.5 rounded-md text-gray-500 hover:text-gray-800 hover:bg-gray-100"
                aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
              >
                {isFullscreen ? <Minimize className="h-5 w-5" /> : <Maximize className="h-5 w-5" />}
              </button>
              
              {isFullscreen && (
                <button 
                  onClick={() => document.exitFullscreen()}
                  className="p-1.5 rounded-md text-gray-500 hover:text-gray-800 hover:bg-gray-100"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>
          
          <div className="prose max-w-none">
            {slides[currentSlide].content}
          </div>

          {slides[currentSlide].image && (
            <div className="flex justify-center mt-4">
              <img src={slides[currentSlide].image} alt="Slide image" className="max-w-full rounded" />
            </div>
          )}
        </div>
      </div>

      {/* Navigation controls - always visible */}
      <div className={`flex items-center justify-between p-4 border-t bg-white ${isFullscreen ? 'mt-auto' : ''}`}>
        <div className="text-sm text-gray-500">
          Slide {currentSlide + 1} of {slides.length}
        </div>
        <div className="flex space-x-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={prevSlide}
            aria-label="Previous slide"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Previous
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={nextSlide}
            aria-label="Next slide"
          >
            Next
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
};

// Video placeholder component
const VideoPlaceholder = () => {
  return (
    <div className="bg-gray-100 rounded-xl overflow-hidden my-10">
      <div className="aspect-video bg-gray-200 flex items-center justify-center relative">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-indigo-500/20"></div>
        <div className="text-center">
          <div className="bg-blue-600 text-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 shadow-lg hover:bg-blue-700 cursor-pointer transition-colors">
            <Play className="h-8 w-8" />
          </div>
          <p className="text-gray-700 font-medium">Watch Full Lecture Video</p>
          <p className="text-sm text-gray-500 mt-1">45 minutes · Premium Content</p>
        </div>
      </div>
    </div>
  );
};

function EpigeneticsCellularMedicine() {
  const [_, navigate] = useLocation();
  
  // Auth check


  // Lecture slides data
  const lectureSlides: Slide[] = [
    {
      id: 1,
      title: "Introduction to Epigenetics in Cellular Medicine",
      content: (
        <>
          <p>Cellular medicine explores how molecular processes inside cells drive health and disease. A key aspect of this is epigenetic regulation – heritable changes in gene expression that do not involve altering the DNA sequence itself. Epigenetic mechanisms act like molecular "switches" and "dimmers," controlling which genes are active or silent in a given cell type.</p>
          
          <p>In this lecture, we will examine the role of chromatin structure, histone modifications, DNA methylation patterns, and other epigenetic marks in regulating gene expression. Fundamental cellular and metabolic pathways are intimately linked to these epigenetic processes – for example, how a cell's nutritional status or metabolic byproducts can directly alter chromatin.</p>
          
          <KeyTakeaway>
            Epigenetic regulation is the key to understanding how identical DNA sequences can give rise to hundreds of different cell types with distinct functions. This regulation not only establishes cell identity during development but continues throughout life in response to environmental factors, diet, stress, and aging.
          </KeyTakeaway>
          
          <p>The term "epigenetics" literally means "above or in addition to genetics." While our genetic code (DNA sequence) does not change much over our lifetime, our epigenetic code is highly dynamic and responsive to both internal and external environments. This dynamism is a double-edged sword – while it allows for cellular adaptability, it also creates vulnerabilities when dysregulated, contributing to diseases from cancer to neurodegeneration.</p>
        </>
      )
    },
    {
      id: 2,
      title: "DNA Transcription and Chromatin Structure",
      content: (
        <>
          <p>All gene expression begins with DNA transcription – the process by which DNA is used as a template to synthesize RNA. However, in eukaryotic cells, DNA is tightly packaged into a complex called chromatin, which profoundly affects transcription. Chromatin consists of DNA wrapped around histone proteins, forming repeating units known as nucleosomes.</p>
          
          <ScientificDiagram 
            title="1. Chromatin Structure and Transcriptional Accessibility" 
            caption="Schematic representation of the hierarchical organization of chromatin and its impact on gene transcription. DNA is wrapped around histone octamers, forming nucleosomes (a). These nucleosomes can be organized into open, accessible euchromatin (b) where transcription factors and RNA polymerase can access gene promoters, or condensed, repressive heterochromatin (c) where DNA is inaccessible to the transcriptional machinery."
          >
            <div className="flex justify-center">
              <svg viewBox="0 0 800 550" className="max-w-full h-auto">
                {/* Background */}
                <rect x="0" y="0" width="800" height="550" fill="#ffffff" />
                
                {/* Main title */}
                <text x="400" y="30" textAnchor="middle" fontSize="20" fontWeight="bold" fill="#333333">Chromatin Structure Hierarchy and Gene Accessibility</text>
                
                {/* A. Nucleosome Structure */}
                <g transform="translate(400, 150)">
                  {/* Histone octamer */}
                  <circle cx="0" cy="0" r="35" fill="#B3DBD8" stroke="#2E8B8B" strokeWidth="2" />
                  
                  {/* DNA wrapped around histone */}
                  <path d="M-35,0 Q-40,-40 0,-40 Q40,-40 35,0 Q40,40 0,40 Q-40,40 -35,0" fill="none" stroke="#4682B4" strokeWidth="6" />
                  <path d="M-35,0 Q-40,-50 0,-50 Q40,-50 35,0 Q40,50 0,50 Q-40,50 -35,0" fill="none" stroke="#4682B4" strokeWidth="6" />
                  
                  {/* Label */}
                  <text x="0" y="0" textAnchor="middle" fontSize="14" fill="#333" fontWeight="bold">Histone</text>
                  <text x="0" y="15" textAnchor="middle" fontSize="14" fill="#333" fontWeight="bold">Octamer</text>
                  
                  {/* Section label */}
                  <text x="0" y="-80" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#333">A. Nucleosome</text>
                </g>
                
                {/* B. Euchromatin (Accessible) */}
                <g transform="translate(200, 350)">
                  {/* Nucleosome array - open form */}
                  <g transform="translate(-120, 0)">
                    <circle cx="0" cy="0" r="12" fill="#B3DBD8" stroke="#2E8B8B" strokeWidth="1" />
                    <path d="M12,0 L40,0" stroke="#4682B4" strokeWidth="3" />
                  </g>
                  
                  <g transform="translate(-70, 0)">
                    <circle cx="0" cy="0" r="12" fill="#B3DBD8" stroke="#2E8B8B" strokeWidth="1" />
                    <path d="M12,0 L40,0" stroke="#4682B4" strokeWidth="3" />
                  </g>
                  
                  <g transform="translate(-20, 0)">
                    <circle cx="0" cy="0" r="12" fill="#B3DBD8" stroke="#2E8B8B" strokeWidth="1" />
                    <path d="M12,0 L40,0" stroke="#4682B4" strokeWidth="3" />
                  </g>
                  
                  <g transform="translate(30, 0)">
                    <circle cx="0" cy="0" r="12" fill="#B3DBD8" stroke="#2E8B8B" strokeWidth="1" />
                    <path d="M12,0 L40,0" stroke="#4682B4" strokeWidth="3" />
                  </g>
                  
                  <g transform="translate(80, 0)">
                    <circle cx="0" cy="0" r="12" fill="#B3DBD8" stroke="#2E8B8B" strokeWidth="1" />
                    <path d="M12,0 L40,0" stroke="#4682B4" strokeWidth="3" />
                  </g>
                  
                  <g transform="translate(130, 0)">
                    <circle cx="0" cy="0" r="12" fill="#B3DBD8" stroke="#2E8B8B" strokeWidth="1" />
                  </g>
                  
                  {/* Transcription factors */}
                  <rect x="-90" y="-40" width="30" height="20" rx="5" fill="#FFB347" stroke="#FF8C00" strokeWidth="1" />
                  <text x="-75" y="-28" textAnchor="middle" fontSize="10" fill="#333">TF</text>
                  
                  <rect x="40" y="-40" width="30" height="20" rx="5" fill="#FFB347" stroke="#FF8C00" strokeWidth="1" />
                  <text x="55" y="-28" textAnchor="middle" fontSize="10" fill="#333">TF</text>
                  
                  {/* RNA Polymerase */}
                  <ellipse cx="-20" cy="-30" rx="20" ry="15" fill="#E6BCB0" stroke="#C16D54" strokeWidth="1" />
                  <text x="-20" y="-26" textAnchor="middle" fontSize="10" fill="#333">RNA Pol</text>
                  
                  {/* Section label */}
                  <text x="0" y="-80" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#333">B. Euchromatin (Accessible)</text>
                  
                  {/* Bracket indicating accessibility */}
                  <path d="M-150,-60 H150" stroke="#1E8449" strokeWidth="2" strokeDasharray="5,3" />
                  <text x="0" y="-65" textAnchor="middle" fontSize="12" fill="#1E8449">High Transcriptional Accessibility</text>
                </g>
                
                {/* C. Heterochromatin (Compact) */}
                <g transform="translate(600, 350)">
                  {/* Nucleosome array - compact form */}
                  <g transform="translate(-100, 30)">
                    <circle cx="0" cy="0" r="12" fill="#B3DBD8" stroke="#2E8B8B" strokeWidth="1" />
                    <path d="M12,0 C20,0 15,-10 25,-10" stroke="#4682B4" strokeWidth="3" />
                  </g>
                  
                  <g transform="translate(-70, 10)">
                    <circle cx="0" cy="0" r="12" fill="#B3DBD8" stroke="#2E8B8B" strokeWidth="1" />
                    <path d="M12,0 C20,0 20,10 28,10" stroke="#4682B4" strokeWidth="3" />
                  </g>
                  
                  <g transform="translate(-40, 35)">
                    <circle cx="0" cy="0" r="12" fill="#B3DBD8" stroke="#2E8B8B" strokeWidth="1" />
                    <path d="M12,0 C20,0 15,-15 25,-15" stroke="#4682B4" strokeWidth="3" />
                  </g>
                  
                  <g transform="translate(-10, 10)">
                    <circle cx="0" cy="0" r="12" fill="#B3DBD8" stroke="#2E8B8B" strokeWidth="1" />
                    <path d="M12,0 C20,0 20,10 28,10" stroke="#4682B4" strokeWidth="3" />
                  </g>
                  
                  <g transform="translate(20, 25)">
                    <circle cx="0" cy="0" r="12" fill="#B3DBD8" stroke="#2E8B8B" strokeWidth="1" />
                    <path d="M12,0 C20,0 15,-10 25,-10" stroke="#4682B4" strokeWidth="3" />
                  </g>
                  
                  <g transform="translate(50, 10)">
                    <circle cx="0" cy="0" r="12" fill="#B3DBD8" stroke="#2E8B8B" strokeWidth="1" />
                    <path d="M12,0 C20,0 20,15 28,15" stroke="#4682B4" strokeWidth="3" />
                  </g>
                  
                  <g transform="translate(80, 30)">
                    <circle cx="0" cy="0" r="12" fill="#B3DBD8" stroke="#2E8B8B" strokeWidth="1" />
                  </g>
                  
                  {/* Repressive factors */}
                  <circle cx="-60" cy="-30" r="12" fill="#D46A6A" stroke="#A14040" strokeWidth="1" />
                  <text x="-60" y="-27" textAnchor="middle" fontSize="10" fill="white">HP1</text>
                  
                  <circle cx="40" cy="-30" r="12" fill="#D46A6A" stroke="#A14040" strokeWidth="1" />
                  <text x="40" y="-27" textAnchor="middle" fontSize="10" fill="white">HP1</text>
                  
                  {/* Section label */}
                  <text x="0" y="-80" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#333">C. Heterochromatin (Condensed)</text>
                  
                  {/* Bracket indicating inaccessibility */}
                  <path d="M-120,-60 H120" stroke="#922B21" strokeWidth="2" strokeDasharray="5,3" />
                  <text x="0" y="-65" textAnchor="middle" fontSize="12" fill="#922B21">Low Transcriptional Accessibility</text>
                </g>
                
                {/* Connecting arrows */}
                <path d="M400,190 L200,250" stroke="#333" strokeWidth="2" markerEnd="url(#arrowhead)" />
                <path d="M400,190 L600,250" stroke="#333" strokeWidth="2" markerEnd="url(#arrowhead)" />
                
                {/* Arrowhead definition */}
                <defs>
                  <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#333" />
                  </marker>
                </defs>
                
                {/* Key functional states */}
                <text x="200" y="490" textAnchor="middle" fontSize="14" fill="#1E8449">Active Genes</text>
                <text x="600" y="490" textAnchor="middle" fontSize="14" fill="#922B21">Silenced Genes</text>
                
                {/* Scale bar */}
                <line x1="50" y1="520" x2="150" y2="520" stroke="#333" strokeWidth="2" />
                <text x="100" y="535" textAnchor="middle" fontSize="12" fill="#333">~100 nm</text>
              </svg>
            </div>
          </ScientificDiagram>
          
          <p>Chromatin structure exists on a spectrum from euchromatin (an open, relaxed form that is transcriptionally active) to heterochromatin (a tightly compacted form that is generally transcriptionally silent). The cell dynamically regulates chromatin compaction to control gene activity.</p>
          
          <p>If a gene's promoter region is wound up in nucleosomes or obscured by tightly packed chromatin, the transcriptional machinery (RNA polymerase and associated factors) cannot easily access it, and the gene remains silent. Conversely, when chromatin is relaxed, transcription factors can bind to their target sites, recruit co-activators, and initiate gene expression.</p>
          
          <KeyTakeaway>
            Two major molecular mechanisms control chromatin accessibility: post-translational modifications of histones and DNA methylation. These modifications are often called "epigenetic marks" or "tags." By adding or removing chemical groups on histone tails or DNA bases, cells can alter how tightly DNA is bound and how it interacts with regulatory proteins.
          </KeyTakeaway>
          
          <p>For example, acetylation of histone tails (attachment of acetyl groups to lysine residues) neutralizes the histone's positive charge, weakening the electrostatic attraction between histones and the negatively charged DNA. This creates a more open chromatin structure that is amenable to transcription. In contrast, histone methylation (addition of methyl groups to specific residues) can either activate or repress transcription, depending on which residue is modified.</p>
        </>
      )
    },
    {
      id: 3,
      title: "Histone Modifications and Chromatin Dynamics",
      content: (
        <>
          <p>The amino-terminal tails of histone proteins are subject to a wide variety of post-translational modifications (PTMs), including acetylation, methylation, phosphorylation, ubiquitination, SUMOylation, and others. These modifications are written onto histones by specific enzymes and erased by others, making chromatin a dynamic signaling platform.</p>
          
          <ScientificDiagram 
            title="2. Histone Modifications and Their Regulatory Effects" 
            caption="Major types of histone post-translational modifications and their effects on chromatin structure and gene expression. The diagram shows how specific enzymes ('writers' and 'erasers') add or remove chemical groups from histone tail residues. Each modification type has distinct effects on chromatin accessibility and transcriptional activity, with acetylation generally promoting gene activation while methylation can be activating or repressive depending on the specific site."
          >
            <div className="flex justify-center">
              <svg viewBox="0 0 800 550" className="max-w-full h-auto">
                {/* Background */}
                <rect x="0" y="0" width="800" height="550" fill="#ffffff" />
                
                {/* Title */}
                <text x="400" y="30" textAnchor="middle" fontSize="20" fontWeight="bold" fill="#333333">Histone Modifications and Regulatory Outcomes</text>
                
                {/* Center nucleosome */}
                <g transform="translate(400, 220)">
                  {/* Histone core */}
                  <circle cx="0" cy="0" r="60" fill="#D6EAF8" stroke="#3498DB" strokeWidth="2" />
                  <text x="0" y="5" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#2874A6">Histone</text>
                  <text x="0" y="25" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#2874A6">Octamer</text>
                  
                  {/* Histone tails */}
                  <path d="M0,-60 C10,-80 30,-85 50,-85" fill="none" stroke="#85C1E9" strokeWidth="4" />
                  <path d="M52,-60 C62,-70 70,-65 85,-60" fill="none" stroke="#85C1E9" strokeWidth="4" />
                  <path d="M60,0 C80,10 85,30 85,40" fill="none" stroke="#85C1E9" strokeWidth="4" />
                  <path d="M0,60 C-10,80 -30,85 -50,85" fill="none" stroke="#85C1E9" strokeWidth="4" />
                  <path d="M-52,60 C-62,70 -70,65 -85,60" fill="none" stroke="#85C1E9" strokeWidth="4" />
                  <path d="M-60,0 C-80,-10 -85,-30 -85,-40" fill="none" stroke="#85C1E9" strokeWidth="4" />
                  
                  {/* DNA wrapping */}
                  <path d="M-42,-42 C-80,-80 -80,80 -42,42" fill="none" stroke="#AED6F1" strokeWidth="8" />
                  <path d="M42,-42 C80,-80 80,80 42,42" fill="none" stroke="#AED6F1" strokeWidth="8" />
                </g>
                
                {/* Acetylation */}
                <g transform="translate(160, 120)">
                  <circle cx="0" cy="0" r="70" fill="#D5F5E3" stroke="#2ECC71" strokeWidth="1" />
                  
                  {/* Header */}
                  <text x="0" y="-45" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#196F3D">Acetylation</text>
                  
                  {/* Content */}
                  <text x="0" y="-20" textAnchor="middle" fontSize="12" fill="#333">H3K27ac, H3K9ac</text>
                  
                  <line x1="-50" y1="-5" x2="50" y2="-5" stroke="#333" strokeWidth="1" />
                  
                  {/* Writer/Eraser */}
                  <text x="-35" y="15" textAnchor="start" fontSize="11" fill="#196F3D">Writers: HATs</text>
                  <text x="-35" y="35" textAnchor="start" fontSize="11" fill="#922B21">Erasers: HDACs</text>
                  
                  {/* Effect */}
                  <text x="0" y="55" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#333">Effect: Gene Activation</text>
                </g>
                
                {/* Methylation */}
                <g transform="translate(640, 120)">
                  <circle cx="0" cy="0" r="70" fill="#FADBD8" stroke="#E74C3C" strokeWidth="1" />
                  
                  {/* Header */}
                  <text x="0" y="-45" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#922B21">Methylation</text>
                  
                  {/* Content */}
                  <text x="0" y="-20" textAnchor="middle" fontSize="12" fill="#333">H3K4me3, H3K27me3</text>
                  
                  <line x1="-50" y1="-5" x2="50" y2="-5" stroke="#333" strokeWidth="1" />
                  
                  {/* Writer/Eraser */}
                  <text x="-35" y="15" textAnchor="start" fontSize="11" fill="#196F3D">Writers: HMTs</text>
                  <text x="-35" y="35" textAnchor="start" fontSize="11" fill="#922B21">Erasers: KDMs</text>
                  
                  {/* Effect */}
                  <text x="0" y="55" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#333">Effect: Context Dependent</text>
                </g>
                
                {/* Phosphorylation */}
                <g transform="translate(160, 320)">
                  <circle cx="0" cy="0" r="70" fill="#D6EAF8" stroke="#3498DB" strokeWidth="1" />
                  
                  {/* Header */}
                  <text x="0" y="-45" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#2874A6">Phosphorylation</text>
                  
                  {/* Content */}
                  <text x="0" y="-20" textAnchor="middle" fontSize="12" fill="#333">H3S10ph, H3T3ph</text>
                  
                  <line x1="-50" y1="-5" x2="50" y2="-5" stroke="#333" strokeWidth="1" />
                  
                  {/* Writer/Eraser */}
                  <text x="-35" y="15" textAnchor="start" fontSize="11" fill="#196F3D">Writers: Kinases</text>
                  <text x="-35" y="35" textAnchor="start" fontSize="11" fill="#922B21">Erasers: Phosphatases</text>
                  
                  {/* Effect */}
                  <text x="0" y="55" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#333">Effect: Mitosis, Signaling</text>
                </g>
                
                {/* Ubiquitination */}
                <g transform="translate(640, 320)">
                  <circle cx="0" cy="0" r="70" fill="#FCF3CF" stroke="#F1C40F" strokeWidth="1" />
                  
                  {/* Header */}
                  <text x="0" y="-45" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#7D6608">Ubiquitination</text>
                  
                  {/* Content */}
                  <text x="0" y="-20" textAnchor="middle" fontSize="12" fill="#333">H2AK119ub, H2BK120ub</text>
                  
                  <line x1="-50" y1="-5" x2="50" y2="-5" stroke="#333" strokeWidth="1" />
                  
                  {/* Writer/Eraser */}
                  <text x="-35" y="15" textAnchor="start" fontSize="11" fill="#196F3D">Writers: E3 ligases</text>
                  <text x="-35" y="35" textAnchor="start" fontSize="11" fill="#922B21">Erasers: DUBs</text>
                  
                  {/* Effect */}
                  <text x="0" y="55" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#333">Effect: Complex Regulation</text>
                </g>
                
                {/* Connecting arrows to nucleosome */}
                <path d="M230,120 L340,170" stroke="#2ECC71" strokeWidth="2" markerEnd="url(#greenArrow)" />
                <path d="M570,120 L460,170" stroke="#E74C3C" strokeWidth="2" markerEnd="url(#redArrow)" />
                <path d="M230,320 L340,270" stroke="#3498DB" strokeWidth="2" markerEnd="url(#blueArrow)" />
                <path d="M570,320 L460,270" stroke="#F1C40F" strokeWidth="2" markerEnd="url(#yellowArrow)" />
                
                {/* Outcome arrows */}
                <g transform="translate(400, 440)">
                  {/* Transcriptional outcomes */}
                  <rect x="-150" y="0" width="300" height="70" rx="10" fill="#f9f9f9" stroke="#333" strokeWidth="1.5" />
                  
                  <text x="0" y="20" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#111">Chromatin State & Transcriptional Outcomes</text>
                  
                  <line x1="-125" y1="30" x2="125" y2="30" stroke="#333" strokeWidth="1" />
                  
                  <text x="-125" y="50" textAnchor="start" fontSize="14" fontWeight="bold" fill="#196F3D">• H3K4me3, H3K27ac: Active</text>
                  <text x="45" y="50" textAnchor="start" fontSize="14" fontWeight="bold" fill="#922B21">• H3K27me3: Repressed</text>
                  
                </g>
                
                {/* Arrow definitions */}
                <defs>
                  <marker id="greenArrow" markerWidth="10" markerHeight="7" refX="10" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#2ECC71" />
                  </marker>
                  <marker id="redArrow" markerWidth="10" markerHeight="7" refX="10" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#E74C3C" />
                  </marker>
                  <marker id="blueArrow" markerWidth="10" markerHeight="7" refX="10" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#3498DB" />
                  </marker>
                  <marker id="yellowArrow" markerWidth="10" markerHeight="7" refX="10" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#F1C40F" />
                  </marker>
                </defs>
              </svg>
            </div>
          </ScientificDiagram>
          
          <p>The pattern of histone marks on a gene – often referred to as a "histone code" – influences whether that gene is active or silent, and how it responds to cellular signals. Key types of histone modifications include:</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-blue-50 p-4 rounded-md">
              <h3 className="font-medium text-blue-900 mb-2">Histone Acetylation</h3>
              <p>Typically associated with gene activation. Acetyl groups are added by HAT enzymes (such as CBP/p300, or PCAF) to lysine residues on histone tails. Acetylation reduces the electrostatic attraction between histones and DNA and creates binding sites for bromodomain-containing proteins that help activate transcription.</p>
            </div>
            <div className="bg-indigo-50 p-4 rounded-md">
              <h3 className="font-medium text-indigo-900 mb-2">Histone Methylation</h3>
              <p>Can either activate or repress transcription depending on the specific amino acid and number of methyl groups added. Tri-methylation of histone H3 on lysine 4 (H3K4me3) at gene promoters is a well-known mark of active transcription; in contrast, tri-methylation of H3 lysine 27 (H3K27me3) is a hallmark of silent chromatin.</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-purple-50 p-4 rounded-md">
              <h3 className="font-medium text-purple-900 mb-2">Histone Phosphorylation</h3>
              <p>Phosphorylation of histones (e.g. H3S10ph) can be involved in chromatin condensation during mitosis, but also in transcriptional activation during certain signaling events.</p>
            </div>
            <div className="bg-rose-50 p-4 rounded-md">
              <h3 className="font-medium text-rose-900 mb-2">Histone Ubiquitination</h3>
              <p>Ubiquitination of histone H2B (H2BK120ub) is linked to active transcription and also facilitates downstream H3K4/H3K79 methylation.</p>
            </div>
          </div>
          
          <KeyTakeaway>
            Histone modifications and chromatin remodeling work together as a sophisticated regulatory network. Writers (HATs, HMTs, etc.) deposit marks, erasers (HDACs, HDMs) remove them, and readers (bromodomain, chromodomain, PHD finger proteins, etc.) bind marks to execute downstream effects. This code of marks determines whether a region of chromatin is in an accessible, transcription-ready state or in a condensed, repressive state.
          </KeyTakeaway>
          
          <p>An important concept is that while DNA sequence is static, epigenetic marks are reversible and dynamic. Chromatin can respond within minutes to external cues – for example, adding a growth factor to cells might lead to rapid acetylation of histones at growth-promoting genes.</p>
        </>
      )
    },
    {
      id: 4,
      title: "DNA Methylation and Epigenetic Memory",
      content: (
        <>
          <p>Another major layer of epigenetic regulation is DNA methylation, which involves adding a methyl group to the DNA base cytosine. In mammals, this predominantly occurs at cytosines that precede a guanine – the CpG dinucleotides. Regions of the genome that have high concentrations of CpGs (called CpG islands), often located near gene promoters, are key targets for regulation by methylation.</p>
          
          <ScientificDiagram 
            title="3. DNA Methylation Mechanisms and Functions" 
            caption="The process and regulation of DNA methylation. a) DNA methyltransferases (DNMTs) add methyl groups to cytosine bases, predominantly at CpG sites. b) The TET family of enzymes mediates active demethylation through hydroxymethylation intermediates. c) Methylation patterns influence gene expression through direct interference with transcription factor binding and recruitment of methyl-binding domain proteins that associate with repressive complexes."
          >
            <div className="flex justify-center">
              <svg viewBox="0 0 800 500" className="max-w-full h-auto">
                {/* Background */}
                <rect x="0" y="0" width="800" height="500" fill="#ffffff" />
                
                {/* Title */}
                <text x="400" y="30" textAnchor="middle" fontSize="20" fontWeight="bold" fill="#333333">DNA Methylation Mechanisms</text>
                
                {/* A. DNA Methylation */}
                <g transform="translate(200, 140)">
                  {/* Background */}
                  <rect x="-120" y="-70" width="240" height="140" rx="10" fill="#F5EEF8" stroke="#8E44AD" strokeWidth="1" />
                  
                  {/* Title */}
                  <text x="0" y="-50" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#8E44AD">A. Methylation</text>
                  
                  {/* DNA strand */}
                  <path d="M-100,0 L100,0" stroke="#333" strokeWidth="2" />
                  
                  {/* CG bases */}
                  <rect x="-70" y="-15" width="20" height="15" fill="#AED6F1" />
                  <text x="-60" y="-5" textAnchor="middle" fontSize="10" fill="#333">C</text>
                  
                  <rect x="-50" y="-15" width="20" height="15" fill="#F9E79F" />
                  <text x="-40" y="-5" textAnchor="middle" fontSize="10" fill="#333">G</text>
                  
                  <rect x="30" y="-15" width="20" height="15" fill="#AED6F1" />
                  <text x="40" y="-5" textAnchor="middle" fontSize="10" fill="#333">C</text>
                  
                  <rect x="50" y="-15" width="20" height="15" fill="#F9E79F" />
                  <text x="60" y="-5" textAnchor="middle" fontSize="10" fill="#333">G</text>
                  
                  {/* Methyl Group */}
                  <circle cx="-60" cy="-20" r="8" fill="#8E44AD" />
                  <text x="-60" y="-17" textAnchor="middle" fontSize="8" fill="white">CH₃</text>
                  
                  {/* DNMT enzyme */}
                  <path d="M40,-20 C35,-40 15,-45 0,-30" fill="#D2B4DE" stroke="#8E44AD" strokeWidth="1" />
                  <text x="10" y="-35" textAnchor="middle" fontSize="12" fill="#8E44AD">DNMT</text>
                  
                  {/* SAM */}
                  <rect x="60" y="-40" width="30" height="15" rx="5" fill="#F5B7B1" stroke="#E74C3C" strokeWidth="1" />
                  <text x="75" y="-30" textAnchor="middle" fontSize="10" fill="#E74C3C">SAM</text>
                  
                  {/* Arrow */}
                  <path d="M60,-32 L40,-20" stroke="#333" strokeWidth="1" markerEnd="url(#arrowhead)" />
                  
                  {/* Labels */}
                  <text x="0" y="30" textAnchor="middle" fontSize="12" fill="#333">De novo: DNMT3A/B</text>
                  <text x="0" y="50" textAnchor="middle" fontSize="12" fill="#333">Maintenance: DNMT1</text>
                </g>
                
                {/* B. DNA Demethylation */}
                <g transform="translate(600, 140)">
                  {/* Background */}
                  <rect x="-120" y="-70" width="240" height="140" rx="10" fill="#EBF5FB" stroke="#3498DB" strokeWidth="1" />
                  
                  {/* Title */}
                  <text x="0" y="-50" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#3498DB">B. Demethylation</text>
                  
                  {/* DNA strand */}
                  <path d="M-100,0 L100,0" stroke="#333" strokeWidth="2" />
                  
                  {/* CG bases */}
                  <rect x="-70" y="-15" width="20" height="15" fill="#AED6F1" />
                  <text x="-60" y="-5" textAnchor="middle" fontSize="10" fill="#333">C</text>
                  
                  <rect x="-50" y="-15" width="20" height="15" fill="#F9E79F" />
                  <text x="-40" y="-5" textAnchor="middle" fontSize="10" fill="#333">G</text>
                  
                  <rect x="30" y="-15" width="20" height="15" fill="#AED6F1" />
                  <text x="40" y="-5" textAnchor="middle" fontSize="10" fill="#333">C</text>
                  
                  <rect x="50" y="-15" width="20" height="15" fill="#F9E79F" />
                  <text x="60" y="-5" textAnchor="middle" fontSize="10" fill="#333">G</text>
                  
                  {/* Hydroxymethyl Group */}
                  <circle cx="40" cy="-20" r="8" fill="#3498DB" />
                  <text x="40" y="-17" textAnchor="middle" fontSize="7" fill="white">5hmC</text>
                  
                  {/* TET enzyme */}
                  <path d="M-60,-20 C-55,-40 -35,-45 -20,-30" fill="#AED6F1" stroke="#3498DB" strokeWidth="1" />
                  <text x="-40" y="-35" textAnchor="middle" fontSize="12" fill="#3498DB">TET</text>
                  
                  {/* Labels */}
                  <text x="0" y="30" textAnchor="middle" fontSize="12" fill="#333">Active: TET1/2/3</text>
                  <text x="0" y="50" textAnchor="middle" fontSize="12" fill="#333">5mC → 5hmC → 5fC → 5caC</text>
                </g>
                
                {/* C. Transcriptional Regulation */}
                <g transform="translate(400, 350)">
                  {/* Background */}
                  <rect x="-300" y="-50" width="600" height="120" rx="10" fill="#f9f9f9" stroke="#333" strokeWidth="1.5" />
                  
                  {/* Title */}
                  <text x="0" y="-25" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#111">C. Functional Outcomes of DNA Methylation</text>
                  
                  {/* Content */}
                  <g transform="translate(-200, 20)">
                    <rect x="-50" y="-15" width="100" height="30" rx="5" fill="#FADBD8" stroke="#E74C3C" strokeWidth="1.5" />
                    <text x="0" y="0" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#922B21">Promoter</text>
                    <text x="0" y="25" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#922B21">→ Gene Silencing</text>
                  </g>
                  
                  <g transform="translate(0, 20)">
                    <rect x="-50" y="-15" width="100" height="30" rx="5" fill="#D4EFDF" stroke="#27AE60" strokeWidth="1.5" />
                    <text x="0" y="0" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#196F3D">Enhancer</text>
                    <text x="0" y="25" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#196F3D">→ Regulation</text>
                  </g>
                  
                  <g transform="translate(200, 20)">
                    <rect x="-50" y="-15" width="100" height="30" rx="5" fill="#D6EAF8" stroke="#3498DB" strokeWidth="1.5" />
                    <text x="0" y="0" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#2874A6">Gene Body</text>
                    <text x="0" y="25" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#2874A6">→ Splicing</text>
                  </g>
                </g>
                
                {/* Connection arrows */}
                <path d="M200,210 L300,280" stroke="#8E44AD" strokeWidth="2" markerEnd="url(#purpleArrow)" />
                <path d="M600,210 L500,280" stroke="#3498DB" strokeWidth="2" markerEnd="url(#blueArrow)" />
                
                {/* Arrow definitions */}
                <defs>
                  <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#333" />
                  </marker>
                  <marker id="purpleArrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#8E44AD" />
                  </marker>
                  <marker id="blueArrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#3498DB" />
                  </marker>
                </defs>
              </svg>
            </div>
          </ScientificDiagram>
          
          <p>5-methylcytosine (5mC) is the form of methylated cytosine and is generally associated with transcriptional silencing. When CpG islands in gene promoters become methylated, they typically impede transcription factors from binding and facilitate the recruitment of methyl-CpG binding proteins (MBDs) that assemble repressive complexes.</p>
          
          <p>DNA methylation patterns are established and maintained by a family of enzymes called DNA methyltransferases (DNMTs):</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-blue-50 p-4 rounded-md">
              <h3 className="font-medium text-blue-900 mb-2">De novo Methyltransferases</h3>
              <p>DNMT3A and DNMT3B set new methylation marks on DNA (for instance, during early development when the embryo's DNA is being methylated afresh after the demethylated primordial germ cell stage).</p>
            </div>
            <div className="bg-indigo-50 p-4 rounded-md">
              <h3 className="font-medium text-indigo-900 mb-2">Maintenance Methyltransferase</h3>
              <p>DNMT1 is the maintenance methyltransferase. During DNA replication, it copies methylation patterns onto the new strand by recognizing hemimethylated CpG sites and adding methyl groups to the newly synthesized strand.</p>
            </div>
          </div>
          
          <p>Conversely, there are mechanisms for DNA demethylation, which can be passive (failure to maintain methylation during replication) or active. Active DNA demethylation is carried out by the TET family of enzymes (TET1/2/3), which oxidize 5-methylcytosine to 5-hydroxymethylcytosine (5hmC) and further oxidized forms, ultimately leading to removal of the mark.</p>
          
          <KeyTakeaway>
            DNA methylation is often thought of as a relatively stable mark – part of what locks genes in an off state long-term. Classic examples of epigenetic silencing by DNA methylation include genomic imprinting (where certain genes are methylated and silenced on one parental allele) and X-chromosome inactivation in females (the silenced X chromosome accumulates DNA methylation and other repressive marks).
          </KeyTakeaway>
          
          <p>From a metabolic standpoint, DNA methylation requires a ready supply of the methyl donor S-adenosylmethionine (SAM). SAM is produced from methionine (an essential amino acid) via the one-carbon cycle (which depends on vitamins like folate (B9) and B12). This creates an important connection between nutrition, metabolism, and epigenetic regulation.</p>
        </>
      )
    },
    {
      id: 5,
      title: "Metabolic Regulation of Epigenetics",
      content: (
        <>
          <p>The metabolic state of a cell profoundly influences its epigenetic landscape. Many metabolites act as substrates, cofactors, or regulators for enzymes that modify chromatin. This metabolic-epigenetic crosstalk forms a key mechanism by which cells adapt their gene expression to nutritional status and environmental conditions.</p>
          
          <ScientificDiagram 
            title="4. Metabolic-Epigenetic Crosstalk" 
            caption="Major metabolic pathways and intermediates regulating epigenetic modifications. The diagram illustrates how cellular metabolism directly feeds into chromatin regulation by providing essential substrates and cofactors for epigenetic enzymes. Nutritional status and metabolic fluctuations can alter the availability of these factors, leading to changes in histone modifications and DNA methylation patterns."
          >
            <div className="flex justify-center">
              <svg viewBox="0 0 800 500" className="max-w-full h-auto">
                {/* Background */}
                <rect x="0" y="0" width="800" height="500" fill="#ffffff" />
                
                {/* Title */}
                <text x="400" y="30" textAnchor="middle" fontSize="20" fontWeight="bold" fill="#333333">Metabolic Regulation of Epigenetic Modifications</text>
                
                {/* Central nucleus */}
                <circle cx="400" cy="250" r="120" fill="#FEF5E7" stroke="#EB984E" strokeWidth="2" />
                <text x="400" y="250" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#EB984E">Nucleus</text>
                
                {/* Chromatin */}
                <g transform="translate(400, 240)">
                  <path d="M-50,-30 C-40,-20 -30,-40 -20,-30 C-10,-20 0,-40 10,-30 C20,-20 30,-40 40,-30" fill="none" stroke="#5D6D7E" strokeWidth="3" />
                  <path d="M-50,-10 C-40,0 -30,-20 -20,-10 C-10,0 0,-20 10,-10 C20,0 30,-20 40,-10" fill="none" stroke="#5D6D7E" strokeWidth="3" />
                  <path d="M-50,10 C-40,20 -30,0 -20,10 C-10,20 0,0 10,10 C20,20 30,0 40,10" fill="none" stroke="#5D6D7E" strokeWidth="3" />
                  <path d="M-50,30 C-40,40 -30,20 -20,30 C-10,40 0,20 10,30 C20,40 30,20 40,30" fill="none" stroke="#5D6D7E" strokeWidth="3" />
                </g>
                
                {/* Metabolic pathways and connections */}
                {/* 1. SAM/Methylation */}
                <g transform="translate(180, 100)">
                  <circle cx="0" cy="0" r="50" fill="#D6EAF8" stroke="#3498DB" strokeWidth="2" />
                  <text x="0" y="-15" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#2874A6">Methionine</text>
                  <text x="0" y="5" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#2874A6">Cycle</text>
                  <text x="0" y="25" textAnchor="middle" fontSize="12" fill="#2874A6">SAM → SAH</text>
                  
                  {/* Connection to nucleus for methylation */}
                  <path d="M50,0 L290,180" stroke="#3498DB" strokeWidth="2" strokeDasharray="5,3" />
                  <text x="170" y="80" textAnchor="middle" fontSize="12" fill="#2874A6" transform="rotate(25, 170, 80)">Methyl Groups for</text>
                  <text x="170" y="95" textAnchor="middle" fontSize="12" fill="#2874A6" transform="rotate(25, 170, 95)">DNA/Histone Methylation</text>
                </g>
                
                {/* 2. Acetyl-CoA/Acetylation */}
                <g transform="translate(180, 400)">
                  <circle cx="0" cy="0" r="50" fill="#D5F5E3" stroke="#2ECC71" strokeWidth="2" />
                  <text x="0" y="-15" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#196F3D">Glucose</text>
                  <text x="0" y="5" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#196F3D">Metabolism</text>
                  <text x="0" y="25" textAnchor="middle" fontSize="12" fill="#196F3D">Acetyl-CoA</text>
                  
                  {/* Connection to nucleus for acetylation */}
                  <path d="M50,0 L290,-70" stroke="#2ECC71" strokeWidth="2" strokeDasharray="5,3" />
                  <text x="170" y="-20" textAnchor="middle" fontSize="12" fill="#196F3D" transform="rotate(-20, 170, -20)">Acetyl Groups for</text>
                  <text x="170" y="-5" textAnchor="middle" fontSize="12" fill="#196F3D" transform="rotate(-20, 170, -5)">Histone Acetylation</text>
                </g>
                
                {/* 3. NAD+/Deacetylation */}
                <g transform="translate(620, 400)">
                  <circle cx="0" cy="0" r="50" fill="#FADBD8" stroke="#E74C3C" strokeWidth="2" />
                  <text x="0" y="-15" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#922B21">NAD+</text>
                  <text x="0" y="5" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#922B21">Metabolism</text>
                  <text x="0" y="25" textAnchor="middle" fontSize="12" fill="#922B21">NAD+/NADH</text>
                  
                  {/* Connection to nucleus for deacetylation */}
                  <path d="M-50,0 L-110,-70" stroke="#E74C3C" strokeWidth="2" strokeDasharray="5,3" />
                  <text x="-80" y="-20" textAnchor="middle" fontSize="12" fill="#922B21" transform="rotate(25, -80, -20)">NAD+ for Sirtuins</text>
                  <text x="-80" y="-5" textAnchor="middle" fontSize="12" fill="#922B21" transform="rotate(25, -80, -5)">Histone Deacetylation</text>
                </g>
                
                {/* 4. Alpha-KG/Demethylation */}
                <g transform="translate(620, 100)">
                  <circle cx="0" cy="0" r="50" fill="#F9E79F" stroke="#F1C40F" strokeWidth="2" />
                  <text x="0" y="-15" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#7D6608">TCA</text>
                  <text x="0" y="5" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#7D6608">Cycle</text>
                  <text x="0" y="25" textAnchor="middle" fontSize="12" fill="#7D6608">α-Ketoglutarate</text>
                  
                  {/* Connection to nucleus for demethylation */}
                  <path d="M-50,0 L-110,180" stroke="#F1C40F" strokeWidth="2" strokeDasharray="5,3" />
                  <text x="-80" y="80" textAnchor="middle" fontSize="12" fill="#7D6608" transform="rotate(-25, -80, 80)">α-KG for</text>
                  <text x="-80" y="95" textAnchor="middle" fontSize="12" fill="#7D6608" transform="rotate(-25, -80, 95)">Histone/DNA Demethylation</text>
                </g>
                
                {/* Nutrient inputs */}
                <g transform="translate(75, 100)">
                  <text x="0" y="0" textAnchor="middle" fontSize="12" fill="#333">Methionine, Folate, B12</text>
                  <path d="M50,0 L105,0" stroke="#333" strokeWidth="1" markerEnd="url(#arrowhead)" />
                </g>
                
                <g transform="translate(75, 400)">
                  <text x="0" y="0" textAnchor="middle" fontSize="12" fill="#333">Glucose, Fatty Acids</text>
                  <path d="M50,0 L105,0" stroke="#333" strokeWidth="1" markerEnd="url(#arrowhead)" />
                </g>
                
                <g transform="translate(725, 100)">
                  <text x="0" y="0" textAnchor="middle" fontSize="12" fill="#333">Glutamine, Citrate</text>
                  <path d="M-50,0 L-105,0" stroke="#333" strokeWidth="1" markerStart="url(#arrowhead)" />
                </g>
                
                <g transform="translate(725, 400)">
                  <text x="0" y="0" textAnchor="middle" fontSize="12" fill="#333">Niacin (B3), ATP</text>
                  <path d="M-50,0 L-105,0" stroke="#333" strokeWidth="1" markerStart="url(#arrowhead)" />
                </g>
                
                {/* Legend */}
                <rect x="325" y="440" width="150" height="40" rx="5" fill="#f9f9f9" stroke="#333" strokeWidth="1.5" />
                <text x="400" y="455" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#111">Nutrient-Sensitive</text>
                <text x="400" y="470" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#111">Epigenetic Regulation</text>
                
                {/* Arrow definition */}
                <defs>
                  <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="0" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#333" />
                  </marker>
                </defs>
              </svg>
            </div>
          </ScientificDiagram>
          
          <p>Key metabolic pathways and intermediates that influence epigenetic modifications include:</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-blue-50 p-4 rounded-md">
              <h3 className="font-medium text-blue-900 mb-2">One-Carbon Metabolism</h3>
              <p>Provides methyl groups for DNA and histone methylation through S-adenosylmethionine (SAM). This pathway depends on dietary intake of methionine, folate (vitamin B9), vitamin B12, and other B vitamins. Deficiencies in these nutrients can alter epigenetic marks globally.</p>
            </div>
            <div className="bg-indigo-50 p-4 rounded-md">
              <h3 className="font-medium text-indigo-900 mb-2">Acetyl-CoA Metabolism</h3>
              <p>Acetyl-CoA is the donor for histone acetylation reactions. It is generated from multiple sources, including glucose (via glycolysis and pyruvate dehydrogenase), fatty acid oxidation, and the metabolism of certain amino acids. In times of glucose abundance, increased acetyl-CoA levels can promote histone acetylation.</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-purple-50 p-4 rounded-md">
              <h3 className="font-medium text-purple-900 mb-2">NAD+ Metabolism</h3>
              <p>NAD+ is a cofactor for sirtuin deacetylases (a class of HDACs). During caloric restriction or fasting, the NAD+/NADH ratio increases, activating sirtuins, which then deacetylate histones and other proteins, contributing to the metabolic adaptation to fasting and potentially to some of its longevity benefits.</p>
            </div>
            <div className="bg-rose-50 p-4 rounded-md">
              <h3 className="font-medium text-rose-900 mb-2">TCA Cycle Intermediates</h3>
              <p>Alpha-ketoglutarate (α-KG) is a TCA cycle intermediate that serves as a cofactor for both histone demethylases (specifically, the Jumonji C domain-containing demethylases) and TET enzymes that mediate DNA demethylation. The α-KG/succinate ratio influences the activity of these enzymes.</p>
            </div>
          </div>
          
          <KeyTakeaway>
            These molecular connections between metabolism and epigenetics explain how dietary factors, nutrient availability, and metabolic states can influence gene expression patterns. For example, periods of fasting may boost NAD+ levels, increasing sirtuin activity and altering histone acetylation patterns in ways that affect stress resistance, inflammation, and cellular repair mechanisms.
          </KeyTakeaway>
          
          <p>The metabolic regulation of epigenetics also explains how certain bioactive food compounds can have epigenetic effects. For instance, sulforaphane from cruciferous vegetables can affect histone deacetylase activity, and butyrate (produced by gut bacteria from dietary fiber) is a histone deacetylase inhibitor that promotes histone acetylation.</p>
        </>
      )
    },
    {
      id: 6,
      title: "Epigenetic Dysregulation in Disease",
      content: (
        <>
          <p>Disruptions to normal epigenetic patterns are implicated in numerous diseases. Some conditions involve specific mutations in the epigenetic machinery itself, while others feature more general epigenetic alterations due to environmental exposures, aging, or other stressors.</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-blue-50 p-4 rounded-md">
              <h3 className="font-medium text-blue-900 mb-2">Cancer</h3>
              <p>Cancer cells typically display global DNA hypomethylation (which can activate oncogenes and destabilize the genome) alongside specific hypermethylation of tumor suppressor gene promoters (silencing these protective genes). Mutations in epigenetic regulators (e.g., DNMT3A, TET2, EZH2, IDH1/2) are causative in many hematological malignancies and some solid tumors.</p>
            </div>
            <div className="bg-indigo-50 p-4 rounded-md">
              <h3 className="font-medium text-indigo-900 mb-2">Neurodevelopmental and Neuropsychiatric Disorders</h3>
              <p>Conditions like Rett syndrome (caused by mutations in MECP2, which reads DNA methylation) and Rubinstein-Taybi syndrome (often due to mutations in the HAT enzyme CBP) demonstrate how epigenetic dysregulation can affect brain development and function. Epigenetic mechanisms are also implicated in conditions like autism, depression, and schizophrenia.</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-purple-50 p-4 rounded-md">
              <h3 className="font-medium text-purple-900 mb-2">Metabolic Disorders</h3>
              <p>Obesity, diabetes, and metabolic syndrome involve altered epigenetic marks in key metabolic tissues. For instance, high-fat diets can alter DNA methylation patterns in the hypothalamus, affecting genes involved in appetite regulation. Poor maternal nutrition can induce epigenetic changes in offspring that predispose them to metabolic dysfunction later in life.</p>
            </div>
            <div className="bg-rose-50 p-4 rounded-md">
              <h3 className="font-medium text-rose-900 mb-2">Inflammatory and Autoimmune Diseases</h3>
              <p>Chronic inflammation can induce specific epigenetic changes, while epigenetic dysregulation can alter immune cell function and cytokine production. Altered DNA methylation patterns have been observed in autoimmune conditions like systemic lupus erythematosus, rheumatoid arthritis, and multiple sclerosis.</p>
            </div>
          </div>
          
          <KeyTakeaway>
            The reversible nature of epigenetic modifications makes them attractive therapeutic targets. Various epigenetic drugs have been developed, including DNA methyltransferase inhibitors (e.g., azacitidine, decitabine), histone deacetylase inhibitors (e.g., vorinostat, romidepsin), and emerging agents targeting histone methyltransferases, demethylases, and reader proteins.
          </KeyTakeaway>
          
          <p>Beyond pharmaceuticals, lifestyle and environmental interventions can also influence epigenetic patterns. Exercise, dietary changes (particularly those affecting one-carbon metabolism or providing bioactive food compounds), stress reduction, and improved sleep may all help normalize disrupted epigenetic marks in various contexts.</p>
        </>
      )
    },
    {
      id: 7,
      title: "Epigenetic Biomarkers and Clinical Applications",
      content: (
        <>
          <p>The growing understanding of epigenetic mechanisms has led to the development of epigenetic biomarkers for disease diagnosis, prognosis, and monitoring of therapeutic response. These biomarkers can offer unique insights into disease states and sometimes provide information that is not captured by genetic testing alone.</p>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-medium mb-3">DNA Methylation Biomarkers</h3>
              <p>Several DNA methylation-based tests have entered clinical use:</p>
              <ul className="list-disc pl-6 space-y-2 mt-2">
                <li>The Cologuard test for colorectal cancer screening includes analysis of NDRG4 and BMP3 gene promoter methylation in stool samples.</li>
                <li>The Epi proColon test detects methylated SEPT9 gene promoter fragments in blood plasma as a biomarker for colorectal cancer.</li>
                <li>Methylation patterns at age-associated CpG sites can be used to calculate "epigenetic age" or "DNA methylation age," which predicts all-cause mortality better than chronological age.</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-medium mb-3">Histone Modification Analysis</h3>
              <p>Though more technically challenging to assess in clinical samples than DNA methylation, histone modification patterns can provide valuable information:</p>
              <ul className="list-disc pl-6 space-y-2 mt-2">
                <li>Global levels of specific modifications, like H4K16ac or H4K20me3, have been associated with cancer prognosis and recurrence risk in some studies.</li>
                <li>Circulating nucleosomes in blood, bearing specific histone marks, are being explored as liquid biopsy biomarkers for cancer and other conditions.</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-medium mb-3">Epigenetic Therapeutics</h3>
              <p>Several epigenetic drugs have been approved for clinical use, primarily in oncology:</p>
              <ul className="list-disc pl-6 space-y-2 mt-2">
                <li>DNA methyltransferase inhibitors (DNMTi): azacitidine (Vidaza) and decitabine (Dacogen) are approved for myelodysplastic syndromes and some leukemias.</li>
                <li>Histone deacetylase inhibitors (HDACi): vorinostat (Zolinza), romidepsin (Istodax), belinostat (Beleodaq), and panobinostat (Farydak) are approved for various hematological malignancies.</li>
                <li>IDH inhibitors: ivosidenib and enasidenib target mutant IDH1/2 enzymes that produce the oncometabolite 2-hydroxyglutarate, which inhibits histone and DNA demethylases.</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-medium mb-3">Emerging Clinical Applications</h3>
              <p>The field is rapidly expanding, with several promising directions:</p>
              <ul className="list-disc pl-6 space-y-2 mt-2">
                <li>Combination therapies: Epigenetic drugs may sensitize cancers to immunotherapy, chemotherapy, or targeted therapies by reactivating silenced genes or altering cell states.</li>
                <li>Non-cancer applications: Epigenetic drugs are being investigated for neurodegenerative diseases, inflammatory conditions, and viral infections.</li>
                <li>Precision approaches: Analyzing the epigenetic landscape of an individual's disease may help select the most effective epigenetic therapy or identify patients who will respond best to particular treatments.</li>
              </ul>
            </div>
          </div>
          
          <KeyTakeaway>
            As technologies for analyzing epigenetic modifications become more accessible and affordable, epigenetic biomarkers are likely to play an increasingly important role in disease detection, monitoring, and guiding therapeutic decisions. The dynamic nature of the epigenome, reflecting both genetic predisposition and environmental exposures, makes it a uniquely valuable source of biological information.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 8,
      title: "Future Directions and Conclusion",
      content: (
        <>
          <p>The field of epigenetics in cellular medicine is rapidly evolving, with several exciting frontiers emerging:</p>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-medium mb-3">Single-Cell Epigenomics</h3>
              <p>New technologies allow researchers to profile the epigenetic landscape of individual cells, revealing heterogeneity within tissues and enabling detailed mapping of how epigenetic patterns change during development, disease progression, and treatment response. These approaches include single-cell ATAC-seq (for chromatin accessibility), single-cell CUT&Tag (for histone modifications), and single-cell bisulfite sequencing (for DNA methylation).</p>
            </div>
            
            <div>
              <h3 className="text-xl font-medium mb-3">Spatial Epigenomics</h3>
              <p>Techniques that preserve spatial information about cells within tissues while assessing their epigenetic state are beginning to emerge. These methods will help us understand how the local tissue microenvironment shapes epigenetic regulation and how epigenetic heterogeneity is organized within organs.</p>
            </div>
            
            <div>
              <h3 className="text-xl font-medium mb-3">Multi-Omics Integration</h3>
              <p>Combining epigenomic data with transcriptomic, proteomic, metabolomic, and other -omic data types provides a more comprehensive view of cellular regulation. Such integrative approaches help elucidate how genetic variation, epigenetic modifications, and environmental factors jointly influence gene expression and cellular phenotypes.</p>
            </div>
            
            <div>
              <h3 className="text-xl font-medium mb-3">Targeted Epigenetic Editing</h3>
              <p>CRISPR-based technologies are now being adapted to precisely add or remove specific epigenetic marks at individual genomic loci. These tools allow researchers to test the causal relationships between epigenetic modifications and gene expression, and may eventually lead to therapeutic strategies for precisely correcting abnormal epigenetic patterns in disease.</p>
            </div>
            
            <div>
              <h3 className="text-xl font-medium mb-3">Epigenetic Clocks and Aging</h3>
              <p>DNA methylation-based epigenetic clocks provide a measure of biological age that predicts health outcomes better than chronological age. Current research aims to understand what drives epigenetic aging, how it relates to cellular and organismal senescence, and whether interventions that slow epigenetic aging might extend healthy lifespan.</p>
            </div>
          </div>
          
          <KeyTakeaway>
            Epigenetic regulation is a fundamental aspect of cellular medicine, bridging genome, environment, and phenotype. The dynamic and potentially reversible nature of epigenetic modifications offers unique opportunities for diagnostic and therapeutic interventions. As we deepen our understanding of these mechanisms, we move closer to a future where epigenetic insights help us prevent and treat disease more effectively, and possibly even address aspects of the aging process itself.
          </KeyTakeaway>
          
          <p>In conclusion, the study of epigenetics has transformed our understanding of gene regulation and revealed how cellular processes respond and adapt to their environment. As technological advances continue to make epigenomic analysis more accessible and precise, we can expect epigenetic insights to become increasingly central to preventive, diagnostic, and therapeutic strategies in medicine.</p>
        </>
      )
    }
  ];
  
    return (
      <MentorshipAccessGate>
      <div className="min-h-screen flex flex-col">
      <Navbar />
      <div className="container mx-auto px-4 py-8 flex-grow">
        <div>
          <div className="mb-8">
            <h1 className="text-3xl font-bold font-serif text-[#2C3E50] mb-2">
              Epigenetics in Cellular Medicine
            </h1>
            <p className="text-lg text-[#7F8C8D]">
              DNA Transcription, Chromatin Dynamics, Methylation, and Regulation in Health & Disease
            </p>
            <Separator className="my-4" />
          </div>
          
          <Slideshow slides={lectureSlides} />
        </div>
      </div>
      <Footer />
      </div>
      </MentorshipAccessGate>
    );
  }

export default EpigeneticsCellularMedicine;